import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import FrequencyMetrics from '../../../../components/olymp/FrequencyMetrics';
import DataMappingCard from './DataMappingCard';
import { getOMOPStatusColor, getOMOPStatusLabel } from '../utils/helpers';

/**
 * Expanded Row Component
 * Displays detailed information when a table row is expanded
 *
 * @param {Object} param - Parameter data
 * @param {Object} parameterLists - All parameter lists
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Function} handleOpenListDialog - Handler for opening list dialog
 */
const ExpandedRow = ({
  param,
  parameterLists,
  activeWorkingList,
  handleOpenListDialog,
}) => {
  return (
    <Box sx={{ py: 3, px: 2, backgroundColor: '#f5f5f5' }}>
      {/* Frequency & Availability Section */}
      {param.frequencyMetrics && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, px: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Verfügbarkeit & Häufigkeits-Analyse
            </Typography>
            {param.availableSince && (
              <Chip
                label={`Verfügbar seit ${param.availableSince}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <FrequencyMetrics
            data={param.frequencyMetrics}
            availableSince={param.availableSince}
            sensitivity={param.sensitivity}
            usagePattern={param.usagePattern}
          />
        </Box>
      )}

      {/* Data Mapping Section */}
      {param.dataMapping && (
        <Box sx={{ mb: 2 }}>
          <DataMappingCard dataMapping={param.dataMapping} />
        </Box>
      )}

      {/* Units & Data Sources Section */}
      {param.units && param.units.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
              Einheiten & Datenquellen
            </Typography>

            {/* List all units with their sources */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: param.units.length > 1 ? 1.5 : 0 }}>
              {param.units.map((unitInfo, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.75,
                    backgroundColor: index === 0 ? '#f0f7ff' : '#f5f5f5',
                    borderRadius: 0.75,
                    border: index === 0 ? '1px solid #d0e5ff' : '1px solid #e0e0e0'
                  }}
                >
                  {/* Unit */}
                  <Box
                    sx={{
                      minWidth: 70,
                      textAlign: 'center',
                      px: 1,
                      py: 0.25,
                      backgroundColor: 'white',
                      borderRadius: 0.5,
                      border: '1px solid #d0d0d0'
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {unitInfo.unit}
                    </Typography>
                  </Box>

                  {/* Sources */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                      {unitInfo.sources.join(', ')}
                    </Typography>
                  </Box>

                  {/* Percentage */}
                  {unitInfo.percentage && (
                    <Chip
                      label={`${unitInfo.percentage}%`}
                      size="small"
                      color={index === 0 ? 'primary' : 'default'}
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  )}

                  {/* Primary indicator */}
                  {index === 0 && (
                    <Chip
                      label="Standard"
                      size="small"
                      color="success"
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {/* OMOP normalization info */}
            {param.units.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: 0.75,
                  p: 1,
                  backgroundColor: '#fff8e1',
                  borderRadius: 0.75,
                  border: '1px solid #ffecb3'
                }}
              >
                <InfoIcon color="warning" fontSize="small" sx={{ mt: 0.1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  <strong>Hinweis:</strong> Beim Import in die OMOP-Datenbank werden alle Werte in die Standard-Einheit{' '}
                  <strong>{param.units[0].unit}</strong> konvertiert.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* OMOP Mapping Section */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
          OMOP Mapping
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Status Chip */}
          <Chip
            label={getOMOPStatusLabel(param.omopStatus)}
            color={getOMOPStatusColor(param.omopStatus)}
            size="small"
            sx={{ fontWeight: 600 }}
          />

          {/* Mapping Details */}
          {param.omopMapping?.topMatch ? (
            <>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 0.75,
                backgroundColor: '#f0f7ff',
                borderRadius: 1,
                border: '1px solid #d0e5ff',
                flex: 1,
                minWidth: 'fit-content'
              }}>
                {/* Concept Name */}
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {param.omopMapping.topMatch.conceptName}
                </Typography>

                {/* Vocabulary */}
                <Chip
                  label={param.omopMapping.topMatch.vocabulary}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />

                {/* Concept Code (e.g., LOINC Code) */}
                {param.omopMapping.topMatch.conceptCode && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold">
                      Code:
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontWeight: 600
                    }}>
                      {param.omopMapping.topMatch.conceptCode}
                    </Typography>
                  </Box>
                )}

                {/* Domain */}
                {param.omopMapping.topMatch.domain && (
                  <Chip
                    label={param.omopMapping.topMatch.domain}
                    size="small"
                    color="default"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}

                {/* Concept ID */}
                <Typography variant="caption" color="textSecondary" sx={{
                  borderLeft: '1px solid #d0d0d0',
                  pl: 1.5,
                  fontFamily: 'monospace'
                }}>
                  OMOP ID: {param.omopMapping.topMatch.conceptId}
                </Typography>
              </Box>

              {/* ATHENA Link */}
              <Button
                variant="outlined"
                size="small"
                href={`https://athena.ohdsi.org/search-terms/terms/${param.omopMapping.topMatch.conceptId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                ATHENA öffnen
              </Button>
            </>
          ) : param.omopCode ? (
            <>
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                ID: {param.omopCode}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                href={`https://athena.ohdsi.org/search-terms/terms/${param.omopCode}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                ATHENA öffnen
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary" fontStyle="italic">
              Kein Mapping vorhanden
            </Typography>
          )}
        </Box>
      </Box>

      {/* Actions Bar */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={
            activeWorkingList ? (
              <PlaylistAddCheckIcon />
            ) : (
              <PlaylistAddIcon />
            )
          }
          onClick={e => handleOpenListDialog(param.id, e)}
        >
          {activeWorkingList && parameterLists ? 'Zu Liste' : 'Liste'}
        </Button>
        <Button
          variant="contained"
          size="small"
          component={Link}
          to={`/parameter/${param.id}`}
          endIcon={<OpenInNewIcon />}
        >
          Alle Details
        </Button>
      </Box>
    </Box>
  );
};

export default ExpandedRow;
